import { useContext, useState, useMemo, useEffect } from 'react'
import { TextField, Button, CircularProgress, Divider, Typography } from '@mui/material'
import { useColorScheme } from '@mui/material/styles'
import PasswordStrengthBar from 'react-password-strength-bar'
import { validate as validateEmail } from "react-email-validator"

import { AppContext } from "/providers/app-context"
import GoogleIcon from '/assets/signin-icons/google.svg'
import AppleDarkIcon from '/assets/signin-icons/apple-dark.svg'
import AppleLightIcon from '/assets/signin-icons/apple-light.svg'
import LinkedInIcon from '/assets/signin-icons/linkedin.svg'

import { resetUserPasswordRequest, oauthLogin } from '/providers/auth-api'
import signInWithGoogle from './google'
import signInWithApple from './apple'
import signInWithLinkedIn from './linkedin'
import { credentialsSignin, credentialsSignup } from './credentials'
import StyledPaper from '../paper'

const providers = [
  { id: 'google', name: 'Google', signIn: signInWithGoogle },
  { id: 'apple', name: 'Apple', signIn: signInWithApple },
  { id: 'linkedin', name: 'LinkedIn', signIn: signInWithLinkedIn },
  { id: 'credentials', name: '' },
]

export default function SmartixSignIn() {
  const { mode, systemMode } = useColorScheme()
  const { apiSource, applyLoginResponse } = useContext(AppContext)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(null) // provider id or null
  const [error, setError] = useState('')
  const [loginMode, setLoginMode] = useState("login")
  const [passwordScore, setPasswordScore] = useState(0)
  const [nameError, setNameError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')

  const nameValidationMessage = (value = name) => {
    const trimmed = value.trim()
    if (!trimmed) {
      return 'Name is required.'
    }
    if (trimmed.length < 3) {
      return 'Name must be at least 3 characters.'
    }
    return ''
  }

  const emailValidationMessage = (value) => {
    const trimmed = value.trim()
    if (!trimmed) {
      return 'Email is required.'
    }
    if (!validateEmail(trimmed)) {
      return 'Enter a valid email address.'
    }
    return ''
  }

  const passwordValidationMessage = (value, { requireStrength = false, score } = {}) => {
    const trimmed = value.trim()
    if (!trimmed) {
      return 'Password is required.'
    }
    if (trimmed.length < 8) {
      return 'Password must be at least 8 characters.'
    }
    const effectiveScore = typeof score === 'number' ? score : passwordScore
    if (requireStrength && effectiveScore < 3) {
      return 'Choose a stronger password before continuing.'
    }
    return ''
  }

  const validateEmailField = (value = email) => {
    const message = emailValidationMessage(value)
    setEmailError(message)
    return !message
  }

  const validatePasswordField = (value = password, options = {}) => {
    const message = passwordValidationMessage(value, options)
    setPasswordError(message)
    return !message
  }

  const handleEmailInputChange = (nextValue) => {
    setEmail(nextValue)
    if (emailError) {
      setEmailError(emailValidationMessage(nextValue))
    }
  }

  const handlePasswordInputChange = (nextValue) => {
    setPassword(nextValue)
    if (passwordError) {
      setPasswordError(passwordValidationMessage(nextValue, { requireStrength: loginMode === 'signup' }))
    }
  }

  const handlePasswordScoreChange = (score) => {
    setPasswordScore(score)
    if (loginMode === 'signup' && passwordError) {
      setPasswordError(passwordValidationMessage(password, { requireStrength: true, score }))
    }
  }

  const clearFieldErrors = () => {
    setNameError('')
    setEmailError('')
    setPasswordError('')
  }

  const themeMode = useMemo(() => {
    if (mode === "system") return systemMode
    return mode
  }, [mode, systemMode])

  const validateNameField = (value = name) => {
    const message = nameValidationMessage(value)
    setNameError(message)
    return !message
  }

  const handleNameInputChange = (nextValue) => {
    setName(nextValue)
    if (nameError) {
      setNameError(nameValidationMessage(nextValue))
    }
  }

  const handleProviderSignIn = async (providerId) => {
    clearFieldErrors()
    setError('')
    setLoading(providerId)
    const provider = providers.find(p => p.id === providerId)

    try {
      const token = await provider.signIn({ apiSource })
      const loginResponse = await oauthLogin({ apiSource, token })

      if (loginResponse.authenticated) {
        await applyLoginResponse(loginResponse, { preserveSelectedClient: false })
      } else {
        setError(loginResponse.message)
      }
    } catch (err) {
      setError(err.message || 'Sign-in failed')
    } finally {
      setLoading(null)
    }
  }

  const handleProviderSignUp = async (providerId) => {
    clearFieldErrors()
    setError('')
    setLoading(providerId)

    const provider = providers.find(p => p.id === providerId)

    try {
      const token = await provider.signIn({ apiSource })

      const signUpResponse = await oauthLogin({ apiSource, token })

      if (signUpResponse.authenticated) {
        await applyLoginResponse(signUpResponse, { preserveSelectedClient: false })
      } else {
        setError(signUpResponse.message)
      }

    } catch (err) {
      setError(err.message || 'Sign-up failed')
    } finally {
      setLoading(null)
    }
  }


  const handlePasswordSignIn = async () => {
    const isEmailValid = validateEmailField()
    const isPasswordValid = validatePasswordField()
    if (!isEmailValid || !isPasswordValid) {
      return
    }
    setError('')
    setLoading('credentials')

    const formData = new FormData()
    const trimmedEmail = email.trim()
    formData.append('email', trimmedEmail)
    formData.append('password', password)

    try {
      const response = await credentialsSignin({ formData, apiSource, applyLoginResponse })
      if (response && !response.authenticated)
        if (typeof response.error === "string") setError(response.error)
        else setError(response.error.message)
    } catch (err) {
      setError(err.message || 'Invalid email or password')
    } finally {
      setLoading(null)
    }
  }

  const handlePasswordSignUp = async () => {
    const isEmailValid = validateEmailField()
    const isPasswordValid = validatePasswordField(password, { requireStrength: true, score: passwordScore })
    const isNameValid = validateNameField()
    if (!isEmailValid || !isPasswordValid || !isNameValid) {
      return
    }
    setError('')
    setLoading('credentials')

    const formData = new FormData()
    const trimmedEmail = email.trim()
    const trimmedName = name.trim()
    formData.append('email', trimmedEmail)
    formData.append('password', password)
    formData.append('name', trimmedName)

    try {
      const response = await credentialsSignup({ formData, apiSource, applyLoginResponse })
      if (response && !response.authenticated) setError(response.error.message ?? "Error signing up")
    } catch (err) {
      setError(err.message || 'Signup failed')
    } finally {
      setLoading(null)
    }
  }

  const handlePasswordReset = async () => {
    const isEmailValid = validateEmailField()
    if (!isEmailValid) {
      return
    }
    // Call server to send email

    await resetUserPasswordRequest({ apiSource, email })

    clearFieldErrors()
    setLoginMode("passwordDone")
  }

  const resetPassword = () => {
    clearFieldErrors()
    setLoginMode("password")
    setError(null)
    setLoading(null)
  }
  const signup = () => {
    clearFieldErrors()
    setLoginMode("signup")
    setError(null)
    setLoading(null)
  }
  const cancel = () => {
    clearFieldErrors()
    setLoginMode("login")
    setError(null)
    setLoading(null)
  }

  const submitPasswordSignIn = async e => {
    e.preventDefault()
    await handlePasswordSignIn()
  }

  const submitPasswordReset = async e => {
    e.preventDefault()
    await handlePasswordReset()
  }

  const submitPasswordSignUp = async e => {
    e.preventDefault()
    await handlePasswordSignUp()
  }


  if (loginMode === "login") return (
    <StyledPaper>

      <div className="flex flex-col gap-5 p-10 w-[450px] ">

        {error && <div className='text-red-600 dark:text-rose-400 text-xl text-center'>{error}</div>}

        {/* OAuth Buttons */}
        <div className='flex flex-col gap-3'>
          <Button
            variant='outlined'
            disabled={loading === 'google'}
            onClick={() => handleProviderSignIn('google')}
          >
            <div className='flex flex-row w-full ml-10 gap-5 justify-start'>
              {loading === 'google' ? <CircularProgress size={20} /> :
                <>
                  <img src={GoogleIcon} />
                  <div>Continue with Google</div>
                </>
              }
            </div>
          </Button>

          <Button
            variant='outlined'
            disabled={loading === 'apple'}
            onClick={() => handleProviderSignIn('apple')}
          >
            <div className='flex flex-row w-full ml-10 gap-5 justify-start'>
              {loading === 'apple' ? <CircularProgress size={20} /> :
                <>
                  <img src={themeMode === "dark" ? AppleDarkIcon : AppleLightIcon} />
                  <div>Continue with Apple</div>
                </>
              }
            </div>
          </Button>

          <Button
            variant='outlined'
            disabled={loading === 'linkedin'}
            onClick={() => handleProviderSignIn('linkedin')}
          >
            <div className='flex flex-row w-full ml-10 gap-5 justify-start'>
              {loading === 'linkedin' ? <CircularProgress size={20} /> :
                <>
                  <img src={LinkedInIcon} />
                  <div>Continue with LinkedIn</div>
                </>
              }
            </div>
          </Button>
        </div>

        <Divider>or</Divider>

        {/* Email/Password form */}
        <form className='flex flex-col gap-4' onSubmit={submitPasswordSignIn}>
          <TextField
            label='Email'
            type='email'
            value={email}
            onChange={e => handleEmailInputChange(e.target.value)}
            onBlur={() => validateEmailField()}
            fullWidth
            size='small'
            disabled={loading === 'credentials'}
            error={Boolean(emailError)}
            helperText={emailError}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label='Password'
            type='password'
            value={password}
            onChange={e => handlePasswordInputChange(e.target.value)}
            onBlur={() => validatePasswordField(password, { requireStrength: loginMode === 'signup' })}
            fullWidth
            size='small'
            disabled={loading === 'credentials'}
            error={Boolean(passwordError)}
            helperText={passwordError}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Button
            type='submit'
            variant='contained'
            disabled={loading === 'credentials'}
          >
            {loading === 'credentials' ? <CircularProgress size={20} /> : 'Sign in'}
          </Button>
        </form>

        <Button onClick={resetPassword}>reset password</Button>
        <Button onClick={signup}>New to Smartix?<br />Signup and use for free!</Button>
      </div>
    </StyledPaper>

  )


  if (loginMode === "password") return (
    <StyledPaper>

      <div className="flex flex-col gap-5 p-10 w-[400px] ">

        <div className='text-3xl mb-10'>Please enter your email address</div>

        {/* Email/Password form */}
        <form className='flex flex-col gap-4' onSubmit={submitPasswordReset}>
          <TextField
            label='Email'
            type='email'
            value={email}
            onChange={e => handleEmailInputChange(e.target.value)}
            onBlur={() => validateEmailField()}
            fullWidth
            size='small'
            disabled={loading === 'credentials'}
            error={Boolean(emailError)}
            helperText={emailError}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Button
            type='submit'
            variant='contained'
            disabled={loading === 'credentials'}
          >
            {loading === 'credentials' ? <CircularProgress size={20} /> : 'Send reset'}
          </Button>
        </form>
        <Button onClick={cancel}>cancel</Button>

        <div className='opacity-60'>NOTE: You can also use this form to add a password to a Google / Apple / LinkedIn login so you can can login directly without using those.</div>

      </div>
    </StyledPaper>

  )

  if (loginMode === "passwordDone") return (
    <StyledPaper>

      <div className="flex flex-col gap-5 p-10 w-[400px] ">

        <div className='text-2xl'>Request submitted. If that email address is registered then you will receive an email shortly.</div>
        <Button onClick={cancel}>back to login form</Button>

      </div>
    </StyledPaper>
  )

  if (loginMode === "signup") return (
    <StyledPaper>

      <div className="flex flex-col gap-5 p-10 w-[400px] ">

        {error && <div className='text-red-600 dark:text-rose-400 text-xl text-center'>{error}</div>}

        {/* OAuth Buttons */}
        <div className='flex flex-col gap-3'>
          <Button
            variant='outlined'
            disabled={loading === 'google'}
            onClick={() => handleProviderSignUp('google')}
          >
            <div className='flex flex-row w-full ml-10 gap-5 justify-start'>
              {loading === 'google' ? <CircularProgress size={20} /> :
                <>
                  <img src={GoogleIcon} />
                  <div>Continue with Google</div>
                </>
              }
            </div>
          </Button>

          <Button
            variant='outlined'
            disabled={loading === 'apple'}
            onClick={() => handleProviderSignUp('apple')}
          >
            <div className='flex flex-row w-full ml-10 gap-5 justify-start'>
              {loading === 'google' ? <CircularProgress size={20} /> :
                <>
                  <img src={themeMode === "dark" ? AppleDarkIcon : AppleLightIcon} />
                  <div>Continue with Apple</div>
                </>
              }
            </div>
          </Button>

          <Button
            variant='outlined'
            disabled={loading === 'linkedin'}
            onClick={() => handleProviderSignUp('linkedin')}
          >
            <div className='flex flex-row w-full ml-10 gap-5 justify-start'>
              {loading === 'google' ? <CircularProgress size={20} /> :
                <>
                  <img src={LinkedInIcon} />
                  <div>Continue with LinkedIn</div>
                </>
              }
            </div>
          </Button>
        </div>

        <Divider>or</Divider>

        <div>Enter your email and choose a password</div>

        {/* Email/Password form */}
        <form className='flex flex-col gap-4' onSubmit={submitPasswordSignUp}>
          <TextField
            label='Your Name'
            type='name'
            value={name}
            onChange={e => handleNameInputChange(e.target.value)}
            onBlur={() => validateNameField()}
            fullWidth
            size='small'
            disabled={loading === 'credentials'}
            error={Boolean(nameError)}
            helperText={nameError}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label='Email'
            type='email'
            value={email}
            onChange={e => handleEmailInputChange(e.target.value)}
            onBlur={() => validateEmailField()}
            fullWidth
            size='small'
            disabled={loading === 'credentials'}
            error={Boolean(emailError)}
            helperText={emailError}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label='Password'
            type='password'
            value={password}
            onChange={e => handlePasswordInputChange(e.target.value)}
            onBlur={() => validatePasswordField(password, { requireStrength: true })}
            fullWidth
            size='small'
            disabled={loading === 'credentials'}
            error={Boolean(passwordError)}
            helperText={passwordError}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <div className='opacity-60 text-sm mt-5'>Password strength (good or strong required)</div>
          <PasswordStrengthBar password={password} onChangeScore={handlePasswordScoreChange} />
          <Button
            type='submit'
            variant='contained'
          >
            {loading === 'credentials' ? <CircularProgress size={20} /> : 'Sign up'}
          </Button>
        </form>

        <Button onClick={cancel}>cancel</Button>
      </div>
    </StyledPaper>

  )
}
